import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useQueryParam from "services/hooks/useQueryParam";
import { getVehicleActivityLogsAPI } from "services/api/fleet/vehicles/activitylogs/api";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ActivityLogRow } from "services/api/fleet/vehicles/activitylogs/types";
import { VehicleHistorySearchPageCmp, VehicleHistorySearchPageProps } from "./types";
import { NoResults } from "ui/components/NoResults";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleHistorySearchResults from "./components/VehicleHistorySearchResults/VehicleHistorySearchResults";
import VehicleHistoryTimeline from "./components/VehicleHistoryTimeline/VehicleHistoryTimeline";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { TableViewToggle } from "ui/components/Table/TableViewToggle";

const VehicleHistorySearchPage: VehicleHistorySearchPageCmp = (props: VehicleHistorySearchPageProps) => {
  const { id } = useParams();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(initialFleetVehicleDetailForm);
  const [searchResults, setSearchResults] = useState<DataWithPagination<ActivityLogRow>>(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useQueryParam("view", "timeline", ["table", "timeline"] as const);

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
    }
  }, [id]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (id: string, pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getVehicleActivityLogsAPI(pagination, id);
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  // Promjena statusa preko značke — vozilo se dohvaća ponovno da se značka osvježi.
  const handleStatusChange = useCallback(() => {
    if (id) fetchVehicle(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title="Povijest"
            icon="/media/icons/history.svg"
            iconColor="stroke-[#edeef8]"
            onStatusChange={handleStatusChange}
          />
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          {/* Promjene statusa i premještaji se ne prikazuju zasebno — svaki je zapis u
              dnevniku aktivnosti ispod, u tablici i na vremenskoj crti. */}
          <div className="flex mb-4 justify-end">
            <TableViewToggle
              value={viewMode}
              onChange={(v) => setViewMode(v as "table" | "timeline")}
              options={[
                { value: "table", label: "Tablica", icon: "/media/icons/table.svg" },
                { value: "timeline", label: "Vremenska crta", icon: "/media/icons/history.svg" },
              ]}
            />
          </div>
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema zapisa u povijesti" />
          ) : viewMode === "table" ? (
            <VehicleHistorySearchResults
              searchResults={searchResults}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              vehicle={formValues}
            />
          ) : (
            <VehicleHistoryTimeline data={searchResults.data} vehicle={formValues} />
          )}
        </div>
      </div>
    </>
  );
};

VehicleHistorySearchPage.route = FleetVehicleRoutes.FleetVehicleHistorySearchPageRouter;

export default VehicleHistorySearchPage;
